import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { API, useContent } from "../App";
import { Button } from "../components/ui/button";
import { MessageCircle, Phone, Mail, CalendarDays, Users, BedDouble, Loader2, CheckCircle } from "lucide-react";
import axios from "axios";

export default function PaymentPage() {
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get("id");
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const { content } = useContent();

  const qrCode = content.qr_code || "https://customer-assets.emergentagent.com/job_ambe-rooms-1/artifacts/9v37indd_file_000000004648720ba0d3224b0b32e472.png";

  useEffect(() => {
    if (bookingId) {
      axios.get(`${API}/bookings/${bookingId}`)
        .then(res => setBooking(res.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [bookingId]);

  const getRoomName = (id) => {
    const map = { "deluxe": "Deluxe Room", "super-deluxe": "Super Deluxe Room", "executive": "Executive Room", "premium-suite": "Premium Suite" };
    return map[id] || id;
  };

  const getWhatsAppUrl = () => {
    if (!booking) return "#";
    const msg = `Hello Ambe Palace,

I have completed my booking.

Name: ${booking.customer_name}
Phone: ${booking.phone}
Room Type: ${getRoomName(booking.room_type)}
Check-in: ${booking.check_in}
Check-out: ${booking.check_out}
Persons: ${booking.num_persons}
Rooms: ${booking.num_rooms}
Amount Paid: \u20B9${booking.total_amount?.toLocaleString()}

Payment completed.
Please confirm my booking.`;
    return `https://wa.me/916376696360?text=${encodeURIComponent(msg)}`;
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 px-4">
        <p className="text-slate-500">Booking not found.</p>
        <Link to="/rooms">
          <Button className="bg-amber-600 hover:bg-amber-700 text-white rounded-none" data-testid="back-to-rooms-btn">Back to Rooms</Button>
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="payment-page">
      <section className="bg-slate-900 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400 mb-4 block">Payment</span>
          <h1 className="text-4xl sm:text-5xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Complete Your Payment</h1>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Booking Summary */}
            <div className="bg-white border border-stone-100 p-6 md:p-8" data-testid="booking-summary">
              <div className="flex items-center gap-2 mb-6">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>Booking Summary</h2>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3 pb-3 border-b border-stone-100">
                  <Users className="w-4 h-4 text-amber-600 mt-1 shrink-0" />
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Guest Name</p>
                    <p className="text-sm font-semibold text-slate-800">{booking.customer_name}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 pb-3 border-b border-stone-100">
                  <Phone className="w-4 h-4 text-amber-600 mt-1 shrink-0" />
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Phone</p>
                    <p className="text-sm font-semibold text-slate-800">{booking.phone}</p>
                  </div>
                </div>
                {booking.email && (
                  <div className="flex items-start gap-3 pb-3 border-b border-stone-100">
                    <Mail className="w-4 h-4 text-amber-600 mt-1 shrink-0" />
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Email</p>
                      <p className="text-sm font-semibold text-slate-800">{booking.email}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-3 pb-3 border-b border-stone-100">
                  <BedDouble className="w-4 h-4 text-amber-600 mt-1 shrink-0" />
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Room Type</p>
                    <p className="text-sm font-semibold text-slate-800">{getRoomName(booking.room_type)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 pb-3 border-b border-stone-100">
                  <CalendarDays className="w-4 h-4 text-amber-600 mt-1 shrink-0" />
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Stay Dates</p>
                    <p className="text-sm font-semibold text-slate-800">{booking.check_in} to {booking.check_out}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 pb-3 border-b border-stone-100">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Rooms</p>
                    <p className="text-sm font-semibold text-slate-800">{booking.num_rooms}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Persons</p>
                    <p className="text-sm font-semibold text-slate-800">{booking.num_persons}</p>
                  </div>
                </div>

                {/* Price Breakdown */}
                <div className="pt-2 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Room Cost</span>
                    <span className="font-semibold text-slate-700">{'\u20B9'}{booking.room_cost?.toLocaleString()}</span>
                  </div>
                  {booking.extra_person_charge > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Extra Person Charges</span>
                      <span className="font-semibold text-slate-700">{'\u20B9'}{booking.extra_person_charge?.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">GST (5%)</span>
                    <span className="font-semibold text-slate-700">{'\u20B9'}{booking.gst?.toLocaleString()}</span>
                  </div>
                  <div className="border-t border-amber-200 bg-amber-50 -mx-2 px-2 py-3 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-slate-900">Total Amount Payable</span>
                      <span className="text-2xl font-bold text-amber-600">{'\u20B9'}{booking.total_amount?.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment QR Section */}
            <div className="space-y-6">
              <div className="bg-white border border-stone-100 p-6 md:p-8 text-center" data-testid="payment-qr-section">
                <h2 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Scan to Pay</h2>
                <p className="text-sm text-slate-500 mb-6">
                  Pay the exact amount shown using the QR code below.
                </p>
                <div className="inline-block bg-white p-4 border-2 border-stone-100 mb-6">
                  <img src={qrCode} alt="Payment QR Code" className="w-56 h-auto mx-auto" data-testid="payment-qr-image" />
                </div>
                <p className="text-xs text-slate-400">
                  Scan and pay with any UPI app
                </p>
              </div>

              <div className="bg-white border border-stone-100 p-6 text-center" data-testid="send-receipt-section">
                <p className="text-sm text-slate-600 mb-4">After payment, click below to confirm your booking via WhatsApp</p>
                <a href={getWhatsAppUrl()} target="_blank" rel="noopener noreferrer">
                  <Button
                    className="w-full bg-green-600 hover:bg-green-700 text-white text-xs font-bold uppercase tracking-[0.15em] rounded-none h-12 transition-colors duration-200"
                    data-testid="send-receipt-btn"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Send Receipt on WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
